import { type ReactNode, Suspense, useCallback, useMemo } from 'react';
import { useDeferredValue } from 'react';
import { useSearchParams } from 'next/navigation';
import { APP_URL_PARAMS, UpdateAppParams } from './params';
import { updateUrlParams } from './updateUrlParams';

export type AppUrlParamsProviderProps = {
  children: ReactNode;
  params?: typeof APP_URL_PARAMS;
};

const EMPTY_URL_SEARCH_PARAMS = new URLSearchParams();

function AppUrlParamsProviderInner(props: AppUrlParamsProviderProps) {
  const pageParams = useDeferredValue(
    useSearchParams(),
    EMPTY_URL_SEARCH_PARAMS
  );
  const pushParamState = useCallback(
    (update: Record<string, string | null>) => {
      const url = updateUrlParams(update);
      window.history.pushState(null, '', url.toString());
    },
    []
  );
  const replaceParamState = useCallback(
    (update: Record<string, string | null>) => {
      const url = updateUrlParams(update);
      window.history.replaceState(null, '', url.toString());
    },
    []
  );
  return useMemo(() => {
    const entries = Object.entries(props.params ?? APP_URL_PARAMS);
    let result = (
      <UpdateAppParams.Provider value={[pushParamState, replaceParamState]}>
        {props.children}
      </UpdateAppParams.Provider>
    );
    let i = entries.length;
    // eslint-disable-next-line no-plusplus
    while (i-- > 0) {
      const [key, Context] = entries[i];
      const value = pageParams.get(key);
      result = <Context value={value}>{result}</Context>;
    }
    return result;
  }, [
    pageParams,
    props.children,
    props.params,
    pushParamState,
    replaceParamState,
  ]);
}

export function AppUrlParamsProvider(props: AppUrlParamsProviderProps) {
  return (
    <Suspense fallback={<>{props.children}</>}>
      <AppUrlParamsProviderInner {...props} />
    </Suspense>
  );
}
